import { Component } from '@theme/component';

/**
 * A timeline component that lets a visitor step through a series of moments by
 * clicking a point on the track, using the forward/backward controls, arrow keys,
 * or by swiping on touch devices.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} stage
 * @property {HTMLButtonElement[]} dots
 * @property {HTMLElement[]} cards
 * @property {HTMLElement} [progressFill]
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends {Component<Refs>}
 */
export class TimelineComponent extends Component {
  requiredRefs = ['dots', 'cards'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.resume);

    const { stage } = this.refs;
    stage?.addEventListener('pointerdown', this.#handlePointerDown);

    window.addEventListener('resize', this.#updateProgress);

    this.#syncDotLabels();
    requestAnimationFrame(this.#updateProgress);

    this.resume();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearInterval(this.#interval);
    window.removeEventListener('resize', this.#updateProgress);
  }

  /**
   * Re-sync state after blocks are added, removed, or reordered in the theme editor.
   */
  updatedCallback() {
    super.updatedCallback();

    this.#current = Math.min(this.#current, this.total - 1);
    this.#syncDotLabels();
    this.#applyState();
  }

  /**
   * Copies each moment's year onto its timeline dot. The block settings are
   * only readable from within the block itself (exposed here as a data
   * attribute on the card), not from the section's `section.blocks` loop, so
   * the dot labels are populated from the cards on the client.
   */
  #syncDotLabels() {
    const { dotYears, cards } = this.refs;
    if (!dotYears || !cards) return;

    dotYears.forEach((label, index) => {
      const year = cards[index]?.dataset.timelineYear;
      if (year != null && year !== '') label.textContent = year;
    });
  }

  get total() {
    return this.refs.cards.length;
  }

  /**
   * Selects the moment at the given index.
   * @param {number|string} input
   * @param {Event} [event]
   */
  select(input, event) {
    const index = Math.max(0, Math.min(this.total - 1, parseInt(String(input), 10)));

    if (Number.isNaN(index) || index === this.#current) return;

    event?.preventDefault();

    this.#current = index;
    this.#applyState();
  }

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault();

    const index = this.#current + 1;
    this.select(index >= this.total ? (this.autoplay ? 0 : this.total - 1) : index);
  }

  /** @param {Event} [event] */
  previous(event) {
    event?.preventDefault();

    const index = this.#current - 1;
    this.select(index < 0 ? (this.autoplay ? this.total - 1 : 0) : index);
  }

  pause = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.#interval) return;

    this.#interval = setInterval(() => this.next(), this.autoplay);
  };

  get autoplay() {
    const value = parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(value) ? undefined : value;
  }

  /** @type {number} */
  #current = 0;

  /** @type {number|undefined} */
  #interval;

  /** @type {number|undefined} */
  #swipeStartX;

  #applyState() {
    const { dots, cards, prevButton, nextButton } = this.refs;
    const { current } = this;

    dots.forEach((dot, index) => {
      dot.setAttribute('aria-selected', String(index === current));
    });

    cards.forEach((card, index) => {
      const isCurrent = index === current;
      card.toggleAttribute('hidden', !isCurrent);
      card.setAttribute('aria-hidden', String(!isCurrent));
    });

    if (!this.autoplay) {
      if (prevButton) prevButton.disabled = current === 0;
      if (nextButton) nextButton.disabled = current === this.total - 1;
    }

    this.#updateProgress();
  }

  get current() {
    return this.#current;
  }

  #updateProgress = () => {
    const { progressFill, dots } = this.refs;
    const activeDot = dots[this.#current];

    if (!progressFill || !activeDot) return;

    const track = activeDot.parentElement;
    const trackRect = track?.getBoundingClientRect();
    const dotRect = activeDot.getBoundingClientRect();

    if (!trackRect) return;

    const center = dotRect.left - trackRect.left + dotRect.width / 2;
    progressFill.style.width = `${center}px`;
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.previous(event);
    else if (event.key === 'ArrowRight') this.next(event);
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#swipeStartX = event.clientX;

    const { stage } = this.refs;
    const controller = new AbortController();

    stage?.addEventListener('pointerup', (upEvent) => this.#handlePointerUp(upEvent, controller), {
      signal: controller.signal,
    });
    stage?.addEventListener('pointercancel', () => controller.abort(), { signal: controller.signal });
  };

  /**
   * @param {PointerEvent} event
   * @param {AbortController} controller
   */
  #handlePointerUp = (event, controller) => {
    controller.abort();

    if (this.#swipeStartX === undefined) return;

    const delta = event.clientX - this.#swipeStartX;
    this.#swipeStartX = undefined;

    const SWIPE_THRESHOLD = 40;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;

    if (delta < 0) this.next(event);
    else this.previous(event);
  };
}

if (!customElements.get('timeline-component')) {
  customElements.define('timeline-component', TimelineComponent);
}
